import { useState, useEffect } from "react";
import { getConversionFactor } from "@/infrastructure/unit-conversion/conversionFactorService";

interface ConversionState {
  factor: number | null;
  exists: boolean | null; // null = not checked yet or same unit
  loading: boolean;
}

/**
 * Hook that fetches the conversion factor between two units.
 * Returns { factor, exists, loading }.
 * - If fromUnitId === toUnitId → no conversion needed, factor = 1, exists = null.
 * - If exists === true → factor is the multiplier.
 * - If exists === false → no conversion available, block submission.
 */
export function useConversionFactor(
  fromUnitId: string,
  toUnitId: string | null,
  productId?: string
): ConversionState {
  const [state, setState] = useState<ConversionState>({ factor: null, exists: null, loading: false });

  useEffect(() => {
    // No conversion needed if same unit or missing data
    if (!fromUnitId || !toUnitId || fromUnitId === toUnitId) {
      setState({ factor: 1, exists: null, loading: false });
      return;
    }

    let cancelled = false;
    setState(prev => ({ ...prev, loading: true }));

    getConversionFactor(fromUnitId, toUnitId, productId)
      .then(result => {
        if (!cancelled) {
          setState({ factor: result.factor, exists: result.exists, loading: false });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ factor: null, exists: false, loading: false });
        }
      });

    return () => { cancelled = true; };
  }, [fromUnitId, toUnitId, productId]);

  return state;
}
